<template>
    <div
        ref="anchorRef"
        class="visually-hidden"
    />
    <Popover
        ref="popoverRef"
        v-model:show="show"
        :position="finalPosition"
        :width="width"
        :unclamp-width="unclampWidth"
        :variant="variant"
        :custom-class="customClass"
        v-bind="$attrs"
        focus
        :focus-ids="focusIds"
        :boundary="boundary"
        @hidden="onHidden"
        @shown="onShown"
    >
        <slot />
    </Popover>
</template>

<script lang="ts" setup>
import {
    computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch,
} from 'vue';
import {
    useElementBounding, useMutationObserver, useResizeObserver,
} from '@vueuse/core';
import { clamp } from 'lodash-es';
import { Position } from '@/scripts/types/Position';
import { HIDE_DROPDOWNS_EVENT } from '../../../scripts/constants/events';
import { getScrollParent } from '../../../scripts/helpers/scroll';
import dropdownEventBus from '../../Event/DropdownEventBus';
import { useWindow } from '../../composables/window';
import { useVisibilityObserver } from '../../composables/observer/visibilityObserver';
import { Variant } from './Popover/Variant';
import Popover, { type Position as FinalPosition } from './Popover.vue';

defineOptions({ inheritAttrs: false });

const popoverRef = useTemplateRef<InstanceType<typeof Popover>>('popoverRef');
const popoverHtmlElementRef = computed<HTMLElement|null>(() => popoverRef.value?.root ?? null);
const anchorRef = ref<HTMLDivElement>();

type Props = {
    variant?: Variant[keyof Variant] | null,
    target: string;
    position?: Position;
    customClass?: string;
    offset?: number;
    boundary?: string | HTMLElement,
    boundaryPadding?: number;
    autoWidth?: boolean
    buttonId?: string,
    width?: number,
    unclampWidth?: boolean,
    focusIds?: string[],
};

const props = withDefaults(defineProps<Props>(), {
    position: Position.BOTTOM_RIGHT,
    offset: 5,
    boundary: 'body',
    boundaryPadding: 50,
    customClass: undefined,
    variant: Variant.PRIMARY,
    autoWidth: false,
    buttonId: undefined,
    width: undefined,
    unclampWidth: false,
    focusIds: () => [],
});

const show = defineModel<boolean>('show', { default: false });

type Emits = {
    (event: 'shown'): void;
    (event: 'hidden'): void;
};

const emit = defineEmits<Emits>();

const finalPosition = ref<FinalPosition>({ top: 0, left: 0 });

const windowScreen = useWindow();

const targetScrollElement = ref<HTMLElement|null>(null);

const targetElement = ref<HTMLElement|null>(null);

const targetBounding = useElementBounding(targetElement);

const targetLayoutElement = computed<HTMLElement|null>(() => {
    if (!targetElement.value) {
        return null;
    }

    const { parentElement } = targetElement.value;
    if (
        !parentElement
        || parentElement === document.body
        || parentElement === document.documentElement
    ) {
        return targetElement.value;
    }

    return parentElement;
});

const getTargetElement = () => document.getElementById(props.target);

const clickElementId = computed(() => props.buttonId ?? props.target);
const focusIds = computed(() => [clickElementId.value, ...props.focusIds]);

const calculateRealPosition = (): Position => {
    const popover = popoverHtmlElementRef.value;
    if (!popover) {
        return props.position;
    }

    const verticalMargin = props.offset;
    const horizontalPadding = props.boundaryPadding;

    const popoverHeight = popover.offsetHeight;
    const popoverWidth = popover.offsetWidth;
    const targetY = targetBounding.top.value;
    const targetX = targetBounding.left.value;
    const targetHeight = targetBounding.height.value;
    const targetWidth = targetBounding.width.value;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const fits = (position: Position): boolean => {
        let top = 0;
        let left = 0;

        if (position === Position.BOTTOM_LEFT) {
            top = targetY + targetHeight + props.offset;
            left = targetX;
        } else if (position === Position.BOTTOM_RIGHT) {
            top = targetY + targetHeight + props.offset;
            left = targetX + targetWidth - popoverWidth;
        } else if (position === Position.TOP_LEFT) {
            top = targetY - popoverHeight - props.offset;
            left = targetX;
        } else if (position === Position.TOP_RIGHT) {
            top = targetY - popoverHeight - props.offset;
            left = targetX + targetWidth - popoverWidth;
        } else if (position === Position.BOTTOM) {
            top = targetY + targetHeight + props.offset;
            left = targetX + targetWidth / 2 - popoverWidth / 2;
        } else if (position === Position.TOP) {
            top = targetY - popoverHeight - props.offset;
            left = targetX + targetWidth / 2 - popoverWidth / 2;
        }

        const topOk = top >= verticalMargin;
        const bottomOk = top + popoverHeight <= viewportHeight - verticalMargin;
        const leftOk = left >= horizontalPadding;
        const rightOk = left + popoverWidth <= viewportWidth - horizontalPadding;

        return topOk && bottomOk && leftOk && rightOk;
    };

    if (fits(props.position)) {
        return props.position;
    }

    const fallbackOrder: Position[] = [
        Position.BOTTOM_RIGHT,
        Position.BOTTOM_LEFT,
        Position.BOTTOM,
        Position.TOP_RIGHT,
        Position.TOP_LEFT,
        Position.TOP,
    ];

    for (const p of fallbackOrder) {
        if (fits(p)) {
            return p;
        }
    }

    return props.position;
};

const getBoundaryOffset = (): { top: number; left: number } => {
    if (props.boundary === 'body' || props.boundary === document.body) {
        const { top, left } = document.body.getBoundingClientRect();

        return { top: -top, left: -left };
    }

    return { top: 0, left: 0 };
};

const getTopOffset = (position: Position): number => {
    if (targetElement.value === null) {
        return 0;
    }

    if (!popoverHtmlElementRef.value) {
        return 0;
    }

    const { top: boundaryTop } = getBoundaryOffset();

    let offsetWithoutLimits = 0;

    if ([Position.BOTTOM, Position.BOTTOM_LEFT, Position.BOTTOM_RIGHT].includes(position)) {
        offsetWithoutLimits = targetBounding.top.value + targetBounding.height.value
            + props.offset;
    }

    if ([Position.TOP, Position.TOP_LEFT, Position.TOP_RIGHT].includes(position)) {
        offsetWithoutLimits = targetBounding.top.value
            - popoverHtmlElementRef.value.scrollHeight - props.offset;
    }

    const minOffset = props.offset;
    const maxOffset = Math.max(
        minOffset,
        windowScreen.innerHeight.value - popoverHtmlElementRef.value.offsetHeight - props.offset,
    );

    return clamp(offsetWithoutLimits, minOffset, maxOffset) + boundaryTop;
};

const getLeftOffset = (position: Position): number => {
    if (!popoverHtmlElementRef.value) {
        return 0;
    }

    if (targetElement.value === null) {
        return 0;
    }

    const left = targetBounding.left.value;
    const width = targetBounding.width.value;
    const { offsetWidth } = popoverHtmlElementRef.value;

    let offsetWithoutLimits = left + width / 2 - offsetWidth / 2;

    if (position === Position.BOTTOM_LEFT) {
        offsetWithoutLimits = left;
    }

    if (position === Position.BOTTOM_RIGHT) {
        offsetWithoutLimits = left + width - offsetWidth;
    }

    if (position === Position.TOP_LEFT) {
        offsetWithoutLimits = left;
    }

    if (position === Position.TOP_RIGHT) {
        offsetWithoutLimits = left + width - offsetWidth;
    }

    const minOffset = props.boundaryPadding;
    const maxOffset = windowScreen.innerWidth.value - offsetWidth - props.boundaryPadding;

    return clamp(offsetWithoutLimits, minOffset, maxOffset) + getBoundaryOffset().left;
};

const width = computed<number|undefined>(
    () => (props.autoWidth ? targetBounding.width.value : props.width),
);

const updateBoundings = () => {
    if (!show.value) {
        return;
    }
    targetElement.value = getTargetElement();
    targetBounding.update();

    const realPosition = calculateRealPosition();
    finalPosition.value = {
        top: getTopOffset(realPosition),
        left: getLeftOffset(realPosition),
    };
};

let boundingsUpdateFrame: number | null = null;

const scheduleBoundingsUpdate = () => {
    if (boundingsUpdateFrame !== null) {
        return;
    }

    boundingsUpdateFrame = requestAnimationFrame(() => {
        boundingsUpdateFrame = null;
        updateBoundings();
    });
};

const cancelScheduledBoundingsUpdate = () => {
    if (boundingsUpdateFrame === null) {
        return;
    }

    cancelAnimationFrame(boundingsUpdateFrame);
    boundingsUpdateFrame = null;
};

const onHidden = () => {
    emit('hidden');
};

const reacquireTarget = (): boolean => {
    const fresh = getTargetElement();
    if (fresh?.isConnected) {
        targetElement.value = fresh;
        scheduleBoundingsUpdate();
        return true;
    }
    return false;
};

let targetVisibilityObserver: IntersectionObserver | null = null;

const createTargetVisibilityObserver = () => {
    if (targetVisibilityObserver) {
        targetVisibilityObserver.disconnect();
        targetVisibilityObserver = null;
    }

    if (!targetElement.value || !show.value) {
        return;
    }

    if (!targetScrollElement.value) {
        targetScrollElement.value = getScrollParent(targetElement.value);
    }

    targetVisibilityObserver = new IntersectionObserver(
        (entries) => {
            if (!show.value || !entries[0]) {
                return;
            }

            const entry = entries[0];
            if (entry.intersectionRatio === 0) {
                if (targetElement.value && !targetElement.value.isConnected && reacquireTarget()) {
                    return;
                }
                show.value = false;
            }
        },
        {
            root: targetScrollElement.value,
            threshold: [0, 1],
            rootMargin: '0px',
        },
    );

    targetVisibilityObserver.observe(targetElement.value);
};

const stopTargetVisibilityObserver = () => {
    if (targetVisibilityObserver) {
        targetVisibilityObserver.disconnect();
        targetVisibilityObserver = null;
    }
};

const onShown = async () => {
    emit('shown');

    await nextTick();
    updateBoundings();
    createTargetVisibilityObserver();
};

const { stop: stopResizeObserver } = useResizeObserver(popoverHtmlElementRef, () => {
    scheduleBoundingsUpdate();
});

const { stop: stopTargetLayoutResizeObserver } = useResizeObserver(targetLayoutElement, () => {
    scheduleBoundingsUpdate();
});

const { stop: stopTargetLayoutMutationObserver } = useMutationObserver(
    targetLayoutElement,
    () => {
        scheduleBoundingsUpdate();
    },
    {
        attributes: true,
        childList: true,
        subtree: true,
    },
);

const onPopoverRelatedEvents = () => {
    scheduleBoundingsUpdate();
};

useVisibilityObserver(anchorRef, {
    onHidden: () => {
        show.value = false;
    },
});

watch(targetElement, () => {
    if (targetElement.value) {
        targetScrollElement.value = getScrollParent(targetElement.value);
        if (show.value) {
            nextTick(() => {
                createTargetVisibilityObserver();
            });
        }
    } else {
        stopTargetVisibilityObserver();
    }
});

const onDocumentScroll = (event: Event) => {
    if (!show.value || !targetElement.value) {
        return;
    }

    const scrollTarget = event.target;

    if (scrollTarget instanceof HTMLElement
        && scrollTarget !== document.body
        && scrollTarget !== document.documentElement) {
        if (scrollTarget.contains(targetElement.value)) {
            onPopoverRelatedEvents();
        }
    }
};

watch(targetScrollElement, (newValue, oldValue) => {
    if (oldValue && (newValue !== oldValue)) {
        oldValue.removeEventListener('scroll', onPopoverRelatedEvents);
    }

    if (targetScrollElement.value && targetScrollElement.value !== document.body) {
        targetScrollElement.value.addEventListener('scroll', onPopoverRelatedEvents, { passive: true });
    }

    if (show.value) {
        nextTick(() => {
            createTargetVisibilityObserver();
        });
    }
});

watch(show, async () => {
    if (show.value) {
        await nextTick();
        if (!targetElement.value) {
            targetElement.value = getTargetElement();
            if (targetElement.value) {
                targetScrollElement.value = getScrollParent(targetElement.value);
            }
        }
        createTargetVisibilityObserver();
    } else {
        stopTargetVisibilityObserver();
    }
});

onMounted(() => {
    updateBoundings();

    if (show.value && targetElement.value) {
        nextTick(() => {
            createTargetVisibilityObserver();
        });
    }

    window.addEventListener('resize', onPopoverRelatedEvents, { passive: true });
    window.addEventListener('scroll', onPopoverRelatedEvents, { passive: true });
    document.addEventListener('scroll', onDocumentScroll, { passive: true, capture: true });

    dropdownEventBus.on(HIDE_DROPDOWNS_EVENT, (id) => {
        if (show.value && id !== props.target) {
            show.value = false;
        }
    });
});

onUnmounted(() => {
    dropdownEventBus.off(HIDE_DROPDOWNS_EVENT);
    window.removeEventListener('resize', onPopoverRelatedEvents);
    window.removeEventListener('scroll', onPopoverRelatedEvents);
    document.removeEventListener('scroll', onDocumentScroll, { capture: true });
    stopResizeObserver();
    stopTargetLayoutResizeObserver();
    stopTargetLayoutMutationObserver();
    stopTargetVisibilityObserver();
    cancelScheduledBoundingsUpdate();

    if (targetScrollElement.value && targetScrollElement.value !== document.body) {
        targetScrollElement.value.removeEventListener('scroll', onPopoverRelatedEvents);
    }
});
</script>

<style lang="scss" scoped>
@import '../../../styles/abstracts/spacings';
@import '../../../styles/abstracts/variables';
@import '../../../styles/abstracts/font-sizes';
@import '../../../styles/abstracts/z-indexes';

.popover {
    position: absolute;
    z-index: $user-info-popup-z-index;

    max-width: 28.75rem;
    padding: 0;

    font-size: $font-size-base;

    border: 0;
    border-radius: $border-radius;
    box-shadow: var(--shadow-4);

    transition: opacity 0.2s ease;
}

.popover--primary {
    background-color: var(--theme-color-surface-primary-default);
}

.popover--secondary {
    background-color: var(--theme-color-surface-inversed-secondary);
}

.popover-enter-from,
.popover-leave-to {
    opacity: 0;
}
</style>
